import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { categories } from '@/mocks/categories';
import { news } from '@/mocks/news';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsListItem from '@/components/feature/NewsListItem';
import Pagination from '@/components/feature/Pagination';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';
const PAGE_SIZE = 5;

export default function CategoryPage() {
  const { type = '' } = useParams();
  const category = categories.find((c) => c.key === type);

  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [tag, setTag] = useState('all');
  const [page, setPage] = useState(1);

  const baseList = useMemo(() => news.filter((n) => n.category === type), [type]);
  const tags = useMemo(() => Array.from(new Set(baseList.flatMap((n) => n.tags))), [baseList]);

  const filtered = useMemo(() => {
    const list = baseList.filter((n) => tag === 'all' || n.tags.includes(tag));
    return [...list].sort((a, b) =>
      sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1
    );
  }, [baseList, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  usePageMeta(
    category ? `${category.name} - 91.con娱乐资讯` : '资讯分类 - 91.con娱乐资讯',
    category
      ? `${category.desc}在 91.con 了解${category.name}的最新动态与热点话题。`
      : '91.con 资讯分类页，浏览影视、综艺、八卦、音乐与明星动态等娱乐资讯。',
    category
      ? `${category.name},91.con,娱乐资讯,影视综艺,热点新闻`
      : '娱乐资讯,91.con,影视新闻,综艺资讯,娱乐八卦'
  );

  if (!category) {
    return (
      <section className={`${container} py-20`}>
        <div className="text-center max-w-lg mx-auto">
          <i className="ri-emotion-sad-line text-5xl text-foreground-500" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">未找到该分类</h1>
          <p className="text-sm text-foreground-600 mt-2">该资讯分类不存在，试试下面的频道：</p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {categories.map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                className="px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:text-primary-400 hover:border-primary-500/60 transition-colors cursor-pointer"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '资讯', to: '/category/film' }, { label: category.name }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-4">
            {category.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-3xl leading-relaxed">{category.desc}</p>
          <div className="flex items-center gap-2 mt-5 flex-wrap">
            {categories.map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap border ${
                  c.key === category.key
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 border-background-200 text-foreground-700 hover:border-primary-500/60'
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-5 border-b border-background-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1">排序：</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              <button
                type="button"
                onClick={() => { setSort('latest'); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'latest' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => { setSort('hot'); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'hot' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                最热
              </button>
            </div>
          </div>
          <p className="text-sm text-foreground-500">
            共 <span className="text-primary-400 font-semibold">{filtered.length}</span> 条资讯
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-5">
          <span className="text-sm text-foreground-500 mr-1">标签：</span>
          <button
            type="button"
            onClick={() => { setTag('all'); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap border ${
              tag === 'all'
                ? 'bg-accent-500 text-background-50 border-accent-500'
                : 'bg-background-100 border-background-200 text-foreground-700 hover:border-accent-500/60'
            }`}
          >
            全部标签
          </button>
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTag(t); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap border ${
                tag === t
                  ? 'bg-accent-500 text-background-50 border-accent-500'
                  : 'bg-background-100 border-background-200 text-foreground-700 hover:border-accent-500/60'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4 mt-8">
          {paged.length > 0 ? (
            paged.map((item) => <NewsListItem key={item.id} item={item} />)
          ) : (
            <div className="text-center py-16">
              <i className="ri-file-search-line text-4xl text-foreground-500" />
              <p className="text-sm text-foreground-600 mt-3">当前筛选条件下暂无资讯，换个标签试试吧</p>
            </div>
          )}
        </div>

        <Pagination current={current} total={totalPages} onChange={setPage} />
      </section>
    </>
  );
}